/**
 * Content for the service pages (/services/:slug), rendered by Components/ServicePage.jsx.
 * Every page shares the same sections; `feature` adds the one section that is specific to a service.
 *
 * - legacyIds: older ids that still reach the page (home ?service= links, former popups)
 * - contactInterest: value sent with the enquiry form (must be one of CONTACT_SERVICE_INTERESTS)
 * - shopCategory: shop category whose products are listed on the page (optional)
 */

const IMG = '/assets/img/service'

export const SERVICE_PAGES = [
  {
    slug: 'starlink',
    legacyIds: ['starlink-installation'],
    name: 'Starlink Internet',
    menuLabel: 'Starlink Internet',
    icon: 'fa-satellite-dish',
    summary: 'Kits, professional installation and activation',
    contactInterest: 'Starlink & Networking',
    hero: {
      rotate: ['homes', 'offices', 'farms', 'schools', 'lodges', 'remote sites'],
      eyebrow: 'Starlink supply & installation',
      title: 'High-speed internet,',
      accent: 'anywhere in Rwanda',
      text: 'We supply genuine Starlink kits, mount and align the dish, activate your service and connect every device — for homes, businesses, farms and remote sites.',
      image: '/assets/img/slider/starlink-hero-hd.jpg',
      badge: { icon: 'fa-bolt', title: 'Online the same day', text: 'Installed by our engineers' },
      primary: { label: 'Shop Starlink kits', shopCategory: 'starlink' },
    },
    facts: [
      { icon: 'fa-certificate', title: 'Genuine kits', text: 'Standard & Mini with warranty' },
      { icon: 'fa-screwdriver-wrench', title: 'Pro installation', text: 'Roof, wall or pole mounting' },
      { icon: 'fa-signal', title: 'Signal optimised', text: 'Clear sky view, best speeds' },
      { icon: 'fa-headset', title: 'Local support', text: 'A team in Kigali you can call' },
    ],
    offers: {
      chip: 'What we offer',
      title: 'Built for how you',
      accent: 'connect',
      intro: 'From a family home in Kigali to a lodge far from the fibre network — we size the right kit and install it properly.',
      items: [
        { icon: 'fa-house-signal', title: 'Homes & families', text: 'Stream, study and work from home on fast, unlimited internet.', image: `${IMG}/starlink-cards/speeds.jpg` },
        { icon: 'fa-building', title: 'Businesses & offices', text: 'Reliable primary or backup internet for POS, cloud tools and meetings.', image: `${IMG}/starlink-cards/resilient-weather.jpg` },
        { icon: 'fa-tree', title: 'Rural & remote sites', text: 'Farms, lodges, schools and clinics where fibre and 4G fall short.', image: `${IMG}/starlink-cards/rural.jpg` },
        { icon: 'fa-person-hiking', title: 'Travel & temporary sites', text: 'Portable Mini kits for projects, events and life on the move.', image: `${IMG}/starlink-cards/roam.jpg` },
      ],
    },
    feature: {
      type: 'kits',
      chip: 'Choose your kit',
      title: 'Standard or Mini —',
      accent: 'which one fits?',
      intro: 'Both connect to the same Starlink network. The difference is size, power and where you will use it.',
      kits: [
        {
          name: 'Starlink Standard Kit',
          match: /starlink\s+kit/i,
          tag: 'Most popular',
          bestFor: 'Homes, offices and fixed sites',
          points: ['Full-size dish for the strongest performance', 'Wi-Fi router included for whole-home coverage', 'Permanent roof, wall or pole mount', 'Powered from the mains'],
        },
        {
          name: 'Starlink Mini',
          match: /starlink\s+mini(?!\s*router)/i,
          tag: 'Portable',
          bestFor: 'Travel, small spaces and temporary sites',
          points: ['Compact dish that fits in a backpack', 'Built-in Wi-Fi — no separate router', 'Runs from DC power or a power bank', 'Set up in minutes, anywhere'],
        },
      ],
      note: 'Not sure? Tell us where you will use it and we will recommend the right kit and mounting.',
    },
    tracker: { icon: 'fa-truck-fast', label: 'Starlink installation' },
    steps: [
      { icon: 'fa-location-dot', when: 'Day 1', title: 'Tell us your site', text: 'Share your location and needs — we confirm coverage and the right kit.', gets: 'Coverage check & kit advice' },
      { icon: 'fa-box-open', when: 'Day 1', title: 'Choose your kit', text: 'Buy online or with our team; we prepare mounts and cabling.', gets: 'Genuine kit, ready to install' },
      { icon: 'fa-satellite-dish', when: 'Same day', title: 'We install & activate', text: 'Dish mounted, aligned and activated; your devices connected.', gets: 'Fast internet on all devices' },
      { icon: 'fa-headset', when: 'Ongoing', title: 'Ongoing support', text: 'Call our Kigali team any time you need help or an upgrade.', gets: 'A local team you can call' },
    ],
    shopCategory: 'starlink',
    productsTitle: 'Starlink kits & accessories',
    faqs: [
      { q: 'Does Starlink work everywhere in Rwanda?', a: 'Starlink works across Rwanda wherever the dish has a clear view of the sky. Trees, tall buildings or walls in the way can slow it down, so we check the best position during installation.' },
      { q: 'Do I need a monthly subscription?', a: 'Yes. The kit is a one-time purchase and the internet service is paid to Starlink monthly. We help you choose a plan and activate it during installation.' },
      { q: 'Can I install it myself?', a: 'You can, but professional installation gives you a secure mount, neat cabling and the best signal. Our engineers also connect and test your devices before they leave.' },
      { q: 'What if something stops working?', a: 'Call or WhatsApp our team. Most issues are solved remotely; if not, we visit your site. Genuine kits bought from us come with warranty support.' },
    ],
    cta: { title: 'Ready to get connected?', text: 'Tell us where you need internet — we will recommend the right kit and give you an installation quote.' },
  },
  {
    slug: 'digital-security',
    legacyIds: ['security'],
    name: 'Digital Security',
    menuLabel: 'Digital Security',
    icon: 'fa-shield-halved',
    summary: 'CCTV, access control, alarms & monitoring',
    contactInterest: 'Digital Security',
    hero: {
      rotate: ['homes', 'shops', 'offices', 'warehouses', 'schools', 'compounds'],
      eyebrow: 'Smarter security, greater peace of mind',
      title: 'Protect what matters',
      accent: 'most',
      text: 'CCTV surveillance, access control, alarms and remote monitoring — designed for your site and installed by our engineers, with local support after go-live.',
      image: '/assets/img/slider/security-hero-hd.jpg',
      badge: { icon: 'fa-mobile-screen', title: 'View cameras on your phone', text: 'Set up during handover' },
      primary: { label: 'Book a free site survey', enquire: true },
    },
    facts: [
      { icon: 'fa-video', title: 'Clear coverage', text: 'Day & night cameras' },
      { icon: 'fa-fingerprint', title: 'Controlled access', text: 'Cards, biometrics & logs' },
      { icon: 'fa-bell', title: 'Early alerts', text: 'Sensors & alarm notifications' },
      { icon: 'fa-headset', title: 'Local support', text: 'Maintenance from Kigali' },
    ],
    offers: {
      chip: 'Our security solutions',
      title: 'Every angle',
      accent: 'covered',
      intro: 'From your first camera to a fully connected system, we build the protection your space needs.',
      items: [
        { icon: 'fa-video', title: 'CCTV surveillance', text: 'Cameras positioned to cover the places that matter, with recording and night vision.', image: `${IMG}/security-cards/cctv-dual-web.jpg`, points: ['IP cameras & NVRs', 'Night vision', 'Remote viewing'] },
        { icon: 'fa-lock', title: 'Smart access control', text: 'Give the right people access to the right spaces, with a clear record of who comes in.', image: `${IMG}/security-cards/smart-lock-web.jpg`, points: ['Smart locks', 'Card & biometric readers', 'Time attendance'] },
        { icon: 'fa-bell', title: 'Intrusion & alarms', text: 'Sensors and alarms that detect unexpected activity and alert you when it matters.', image: `${IMG}/security-cards/intrusion-alarm.jpg`, points: ['Motion detection', 'Door & window sensors', 'Alarm notifications'] },
        { icon: 'fa-display', title: 'Monitoring & remote access', text: 'All your camera feeds on one screen or your phone, wherever you are.', image: `${IMG}/security-cards/control-room-web.jpg`, points: ['Live feeds', 'Mobile app setup', 'Playback & recordings'] },
      ],
    },
    feature: {
      type: 'categories',
      chip: 'Shop security equipment',
      title: 'Find the right',
      accent: 'equipment',
      intro: 'Browse our security range by type — every product can be supplied on its own or fully installed.',
      shopCategory: 'digital-security',
      packages: [
        { icon: 'fa-house', title: 'Home', text: 'Entrance, perimeter and garden covered, with live view on your phone.', points: ['2–4 cameras', 'Recorder & storage', 'Mobile app'] },
        { icon: 'fa-store', title: 'Shop & office', text: 'Cameras, access control and alarms for staff, stock and customers.', points: ['4–8 cameras', 'Door access control', 'Intrusion alarm'], featured: true },
        { icon: 'fa-industry', title: 'Compound & enterprise', text: 'Large sites, gates and multiple buildings, monitored from one place.', points: ['8+ cameras', 'Gate automation', 'Central monitoring'] },
      ],
    },
    tracker: { icon: 'fa-truck-fast', label: 'Security installation' },
    steps: [
      { icon: 'fa-magnifying-glass-location', when: 'Day 1', title: 'We assess your space', text: 'We walk your site and identify entrances, blind spots and priority areas.', gets: 'Free site survey' },
      { icon: 'fa-file-signature', when: 'Day 2', title: 'We design the system', text: 'A clear proposal with the right cameras, access and alarms for your budget.', gets: 'Written proposal & quote' },
      { icon: 'fa-screwdriver-wrench', when: '1–3 days', title: 'We install & configure', text: 'Neat installation, recording and alerts set up, apps on your phone.', gets: 'Working, tested system' },
      { icon: 'fa-user-shield', when: 'Handover', title: 'You take control', text: 'We train your team and stay available for support and maintenance.', gets: 'Training & after-sales support' },
    ],
    shopCategory: 'digital-security',
    productsTitle: 'Popular security products',
    faqs: [
      { q: 'Which security system is right for my property?', a: 'It depends on your layout, the areas you need to protect and how you want to manage access. We start with a site assessment and recommend a combination of cameras, access control and alarms that fits your needs and budget.' },
      { q: 'Can I view my cameras on my phone?', a: 'Yes. With compatible cameras and recorders we configure a mobile app for live viewing and recordings. Remote access needs a working internet connection, and we explain the setup during handover.' },
      { q: 'Can you improve an existing security system?', a: 'We assess your current cameras, recorder and cabling to see what can be reused. Compatibility and condition decide whether an upgrade or a replacement is the better option.' },
      { q: 'What happens after installation?', a: 'We test everything, show your team how to use it and hand over the system. We also offer after-sales support and maintenance so you always know where to turn.' },
    ],
    cta: { title: 'Peace of mind starts with a conversation', text: 'Tell us what you want to protect — we will visit, assess and propose the right solution.' },
  },
  {
    slug: 'network-infrastructure',
    legacyIds: ['networking'],
    name: 'Network Infrastructure',
    menuLabel: 'Network Infrastructure',
    icon: 'fa-network-wired',
    summary: 'Cabling, switching, Wi-Fi & server rooms',
    contactInterest: 'Starlink & Networking',
    hero: {
      rotate: ['offices', 'hotels', 'schools', 'clinics', 'warehouses', 'NGOs'],
      eyebrow: 'Networking & IT solutions',
      title: 'Networks that keep your',
      accent: 'business moving',
      text: 'Wired and wireless networks designed, installed and supported in Rwanda — so your team works without dropouts.',
      image: '/assets/img/slider/network-hero-hd.jpg',
      badge: { icon: 'fa-circle-check', title: 'Tested before go-live', text: 'Documented for your team' },
      primary: { label: 'Get a free site assessment', enquire: true },
    },
    facts: [
      { icon: 'fa-bolt', title: 'Fewer dropouts', text: 'Stable links for daily work' },
      { icon: 'fa-gauge-high', title: 'Faster networks', text: 'Sized for your real traffic' },
      { icon: 'fa-shield-halved', title: 'Secure by default', text: 'Guest Wi-Fi, VLANs, firewall' },
      { icon: 'fa-headset', title: 'Local support', text: 'Engineers based in Kigali' },
    ],
    offers: {
      chip: 'Capabilities',
      title: 'Built end-to-end for',
      accent: 'your site',
      intro: 'One partner from planning to go-live — so nothing falls between vendors.',
      items: [
        { icon: 'fa-network-wired', title: 'Structured cabling & switching', text: 'Neat, labelled copper and fibre runs with managed switches built for growth.', image: '/assets/img/home/service-structured-cabling.jpg' },
        { icon: 'fa-wifi', title: 'Business Wi-Fi', text: 'Access points placed and tuned so staff and guests stay connected in every room.', image: '/assets/img/home/service-business-wifi.jpg' },
        { icon: 'fa-server', title: 'Racks & server rooms', text: 'Organised racks, UPS-ready layouts and equipment you can maintain.', image: '/assets/img/home/service-racks.jpg' },
        { icon: 'fa-chart-line', title: 'Monitoring & support', text: 'Visibility after go-live — and a team in Kigali when something needs fixing.', image: '/assets/img/home/service-monitoring.jpg' },
      ],
    },
    feature: {
      type: 'audiences',
      chip: 'Who we serve',
      title: 'Ideal for growing',
      accent: 'workplaces',
      intro: 'Whether you are opening a new floor or fixing an unreliable setup, we scale with you.',
      audiences: [
        { label: 'Offices & coworking', image: '/assets/img/home/audience-offices.jpg' },
        { label: 'Hotels & hospitality', image: '/assets/img/home/audience-hotels.jpg' },
        { label: 'Schools & campuses', image: '/assets/img/home/audience-schools.jpg' },
        { label: 'Clinics & pharmacies', image: '/assets/img/home/audience-clinics.jpg' },
        { label: 'Retail & warehouses', image: '/assets/img/home/audience-retail.jpg' },
        { label: 'NGOs & branch sites', image: '/assets/img/home/audience-ngos.jpg' },
      ],
      includesTitle: 'Every Fabritech delivery includes',
      includes: ['On-site assessment and network design', 'Professional cabling, switching & Wi-Fi installation', 'Configuration, testing and documentation', 'Handover training for your team', 'Post-installation support in Rwanda'],
    },
    tracker: { icon: 'fa-truck-fast', label: 'Network project' },
    steps: [
      { icon: 'fa-clipboard-list', when: 'Day 1', title: 'We listen & survey', text: 'A walkthrough of your floors, users and pain points — no cookie-cutter designs.', gets: 'Site survey & needs list' },
      { icon: 'fa-diagram-project', when: 'Day 2–3', title: 'We design', text: 'The right hardware and layout for your space, traffic and budget.', gets: 'Network plan & quote' },
      { icon: 'fa-ethernet', when: '1–2 days', title: 'We install & test', text: 'Clean cabling, configured switches and Wi-Fi, tested before go-live.', gets: 'Tested, labelled network' },
      { icon: 'fa-handshake', when: 'Handover', title: 'We hand over & stay', text: 'Documentation, training and ongoing support so it keeps performing.', gets: 'Docs, training & support' },
    ],
    shopCategory: 'power-backup',
    productsTitle: 'Keep your network powered',
    productsIntro: 'UPS and power backup to protect switches, routers and servers during outages.',
    faqs: [
      { q: 'Can you fix our existing network instead of replacing it?', a: 'Yes. We start by assessing what you have — cabling, switches and Wi-Fi — and reuse what is in good condition. Often a redesign of the Wi-Fi or a few new switches solves the problem.' },
      { q: 'How long does an installation take?', a: 'A small office is usually done in one to two days. Larger sites are planned in phases so your team can keep working during the installation.' },
      { q: 'Do you work outside Kigali?', a: 'Yes. We install and support networks across Rwanda, including branch offices and remote sites.' },
      { q: 'What brands do you install?', a: 'We work with proven business-grade equipment and recommend brands based on your needs and budget. Every installation is documented so any engineer can maintain it.' },
    ],
    cta: { title: 'Let’s design a network your team can trust', text: 'Tell us about your site and challenges — we will propose a practical plan and timeline.' },
  },
  {
    slug: 'canal-dstv',
    legacyIds: ['canalplus-dstv', 'paytv'],
    name: 'Canal+ & DStv Services',
    menuLabel: 'Canal+ & DStv',
    icon: 'fa-tv',
    summary: 'Installation, signal repair & renewals',
    contactInterest: 'Canal+ & DStv Services',
    hero: {
      rotate: ['living rooms', 'hotels', 'bars & restaurants', 'offices'],
      eyebrow: 'Canal+ & DStv installers',
      title: 'Clear TV reception,',
      accent: 'every day',
      text: 'Professional dish installation, decoder setup, signal repairs and subscription renewals — one local team for everything your TV needs.',
      image: '/assets/img/home/service-canal-dstv.jpg',
      badge: { icon: 'fa-rotate', title: 'Renew in minutes', text: 'Send your decoder number' },
      primary: { label: 'Book an installation', enquire: true },
    },
    facts: [
      { icon: 'fa-satellite-dish', title: 'Correct first time', text: 'Dish mounted & aligned' },
      { icon: 'fa-wrench', title: 'Signal repairs', text: 'Weak or lost signal fixed' },
      { icon: 'fa-rotate', title: 'Easy renewals', text: 'Subscription help on WhatsApp' },
      { icon: 'fa-house-chimney', title: 'Multi-room', text: 'TV in every room' },
    ],
    offers: {
      chip: 'Our TV services',
      title: 'Everything your TV',
      accent: 'needs',
      intro: 'From a brand-new installation to a quick renewal, we keep your entertainment running.',
      items: [
        { icon: 'fa-satellite-dish', title: 'New installation', text: 'Dish mounting, cabling and decoder setup for Canal+ and DStv, done properly the first time.' },
        { icon: 'fa-signal', title: 'Signal troubleshooting', text: 'No signal, pixelated picture or lost channels — we diagnose and fix the cause.' },
        { icon: 'fa-tv', title: 'HD & 4K decoder setup', text: 'Decoder configuration and connection to your TV for the best picture quality.' },
        { icon: 'fa-house-chimney', title: 'Multi-room setup', text: 'Watch in more than one room, with neat cabling and extra decoders.' },
        { icon: 'fa-rotate', title: 'Subscription renewal', text: 'Renew or reactivate your subscription quickly with our help.' },
        { icon: 'fa-arrow-up-right-dots', title: 'Package changes', text: 'Upgrade or change your package to the channels you actually watch.' },
      ],
    },
    feature: {
      type: 'quick-actions',
      chip: 'Fast help',
      title: 'What do you',
      accent: 'need today?',
      intro: 'Pick an option — it opens WhatsApp with a message ready to send to our team.',
      actions: [
        { icon: 'fa-satellite-dish', title: 'Install Canal+ or DStv', text: 'Book a technician for a new installation.', message: 'Hello Fabritech, I would like a new Canal+/DStv installation. My location is: ' },
        { icon: 'fa-triangle-exclamation', title: 'Fix my signal', text: 'No signal or a poor picture? We will help.', message: 'Hello Fabritech, I have a problem with my TV signal (Canal+/DStv). Problem: ' },
        { icon: 'fa-rotate', title: 'Renew my subscription', text: 'Send your decoder number to renew.', message: 'Hello Fabritech, I would like to renew my Canal+/DStv subscription. My decoder number is: ' },
      ],
    },
    tracker: { icon: 'fa-truck-fast', label: 'TV technician visit' },
    steps: [
      { icon: 'fa-comments', when: 'Today', title: 'Contact us', text: 'Call, WhatsApp or send the form with what you need.', gets: 'Quick reply from our team' },
      { icon: 'fa-calendar-check', when: 'Your choice', title: 'We schedule a visit', text: 'A technician comes at a time that suits you.', gets: 'Confirmed visit time' },
      { icon: 'fa-satellite-dish', when: 'On the visit', title: 'Install or repair', text: 'Dish, cabling and decoder set up and tested on site.', gets: 'Clear picture, tested' },
      { icon: 'fa-tv', when: 'Anytime', title: 'Enjoy & renew easily', text: 'Message us whenever you need a renewal or help.', gets: 'Easy renewals on WhatsApp' },
    ],
    faqs: [
      { q: 'Do you install both Canal+ and DStv?', a: 'Yes. Our technicians install and repair both, including dishes, cabling, decoders and multi-room setups.' },
      { q: 'My picture keeps freezing — can you fix it?', a: 'Usually yes. Freezing and lost signal are often caused by dish alignment, cabling or weather damage. We test the signal and fix the cause on site.' },
      { q: 'How do I renew my subscription with you?', a: 'Send us your decoder number and the package you want on WhatsApp. We confirm the amount and help you renew quickly.' },
      { q: 'Can I watch on more than one TV?', a: 'Yes. We set up multi-room viewing with extra decoders and neat cabling to each room.' },
    ],
    cta: { title: 'Need a technician?', text: 'Tell us what you need and where — we will schedule a visit at a time that suits you.' },
  },
  {
    slug: 'software-solutions',
    legacyIds: ['software-development', 'software'],
    name: 'Software & Solutions',
    menuLabel: 'Software & Solutions',
    icon: 'fa-code',
    summary: 'Websites, business systems & mobile apps',
    contactInterest: 'Software Development',
    hero: {
      rotate: ['startups', 'shops', 'schools', 'NGOs', 'enterprises'],
      eyebrow: 'Software development',
      title: 'Software built around',
      accent: 'your business',
      text: 'Custom websites, business systems and mobile apps — designed, built and supported by our team in Kigali, from first idea to launch and beyond.',
      image: '/assets/img/slider/software-hero-v2.jpg',
      badge: { icon: 'fa-rocket', title: 'From idea to launch', text: 'And support after go-live' },
      primary: { label: 'Start your project', enquire: true },
    },
    facts: [
      { icon: 'fa-pen-ruler', title: 'Designed for you', text: 'No generic templates' },
      { icon: 'fa-mobile-screen', title: 'Web & mobile', text: 'Works on every device' },
      { icon: 'fa-plug', title: 'Integrations', text: 'Payments, SMS, APIs' },
      { icon: 'fa-life-ring', title: 'Long-term support', text: 'From the team that built it' },
    ],
    offers: {
      chip: 'What we build',
      title: 'Digital tools that',
      accent: 'work for you',
      intro: 'We turn your processes into reliable software — from a company website to a complete business system.',
      items: [
        { icon: 'fa-globe', title: 'Websites & e-commerce', text: 'Fast, professional websites and online shops that bring you customers.', image: `${IMG}/network-cards/laptop-work.jpg` },
        { icon: 'fa-building-columns', title: 'Business systems & ERP', text: 'Inventory, sales, HR, assets and reporting in one system built for your workflow.', image: `${IMG}/network-cards/business-planning.jpg` },
        { icon: 'fa-mobile-screen-button', title: 'Mobile apps', text: 'Android and iOS apps for your customers, staff or field teams.', image: `${IMG}/software.jpeg` },
        { icon: 'fa-plug-circle-check', title: 'Integrations & APIs', text: 'Connect mobile money, SMS, email and the tools you already use.', image: `${IMG}/network-cards/code-screen.jpg` },
      ],
    },
    feature: {
      type: 'showcase',
      chip: 'Our work',
      title: 'Built in Rwanda,',
      accent: 'ready to grow',
      intro: 'From the first line of code to a polished product on every screen — designed, built and tested by our team.',
      imageAlt: 'Code editor, website, mobile app and design tool on a laptop, monitor, phone and tablet',
      image: '/assets/img/slider/software-hero-v2.jpg',
      points: [
        { icon: 'fa-code', title: 'Clean, modern code', text: 'Fast, secure and easy to maintain as you grow.' },
        { icon: 'fa-mobile-screen', title: 'Web & mobile', text: 'Websites and apps that work on every screen.' },
        { icon: 'fa-pen-ruler', title: 'Designed first', text: 'Screens you review and approve before we build.' },
      ],
      techTitle: 'Technologies we use',
      tech: ['React', 'Node.js', 'Flutter', 'PostgreSQL', 'REST APIs', 'Mobile Money', 'Cloud hosting'],
    },
    tracker: { icon: 'fa-rocket', label: 'Software project' },
    steps: [
      { icon: 'fa-lightbulb', when: 'Week 1', title: 'Discover', text: 'We learn your goals, users and processes, and agree the scope.', gets: 'Clear scope & quote' },
      { icon: 'fa-pen-ruler', when: 'Week 1–2', title: 'Design', text: 'Screens and flows you can review before any code is written.', gets: 'Designs you approve' },
      { icon: 'fa-code', when: 'Weeks 2+', title: 'Build & test', text: 'Developed in short cycles, with regular demos and your feedback.', gets: 'Regular demos' },
      { icon: 'fa-rocket', when: 'Launch', title: 'Launch & support', text: 'We deploy, train your team and keep improving the product.', gets: 'Live product & support' },
    ],
    faqs: [
      { q: 'How much does a website or system cost?', a: 'It depends on the features you need. After a short discovery call we send a clear quote with the scope, timeline and price — no surprises.' },
      { q: 'How long does a project take?', a: 'A company website usually takes two to four weeks. Business systems and apps are planned in phases, with a first working version early on.' },
      { q: 'Will I own the software?', a: 'Yes. You own your website or system and its data. We agree hosting and access details before launch.' },
      { q: 'Do you support the software after launch?', a: 'Yes. We offer maintenance and support plans covering updates, fixes, backups and new features as your business grows.' },
    ],
    cta: { title: 'Have a project in mind?', text: 'Tell us what you want to build — we will reply with ideas, a timeline and a quote.' },
  },
  {
    slug: 'training-center',
    legacyIds: ['internships-courses', 'training'],
    name: 'Training Center',
    menuLabel: 'Training Center',
    icon: 'fa-graduation-cap',
    summary: 'Internships & practical short courses',
    contactInterest: 'Internships & Short Courses',
    hero: {
      rotate: ['students', 'graduates', 'career changers', 'tech teams'],
      eyebrow: 'Internships & short courses',
      title: 'Learn by doing,',
      accent: 'grow your career',
      text: 'Academic and professional internships plus practical short courses — learn on real projects with mentors from our engineering and software teams.',
      image: `${IMG}/internship-training-pro.jpg`,
      badge: { icon: 'fa-award', title: 'Certificate of completion', text: 'For every finished programme' },
      primary: { label: 'Apply now', enquire: true },
    },
    facts: [
      { icon: 'fa-diagram-project', title: 'Real projects', text: 'Not just theory' },
      { icon: 'fa-user-tie', title: 'Mentorship', text: 'From working professionals' },
      { icon: 'fa-award', title: 'Certificate', text: 'On completion' },
      { icon: 'fa-calendar-check', title: 'Flexible', text: 'Fits school & work schedules' },
    ],
    offers: {
      chip: 'Programmes',
      title: 'Two ways to',
      accent: 'learn with us',
      intro: 'Choose an internship to gain workplace experience, or a short course to learn a specific skill.',
      items: [
        { icon: 'fa-user-graduate', title: 'Academic internships', text: 'For university and TVET students who need placement credit and practical experience.', image: `${IMG}/internship-learning.jpg`, points: ['School placement', 'Supervisor reports', 'Real assignments'] },
        { icon: 'fa-briefcase', title: 'Professional internships', text: 'For graduates and career changers building job-ready skills and a portfolio.', image: `${IMG}/internship-team.jpg`, points: ['Portfolio projects', 'Mentorship', 'Career guidance'] },
      ],
    },
    feature: {
      type: 'courses',
      chip: 'Short courses',
      title: 'Practical skills,',
      accent: 'taught hands-on',
      intro: 'Courses are built around the work our teams do every day. Ask us for the next start dates.',
      courses: [
        { icon: 'fa-network-wired', title: 'Networking & IT support', text: 'Cabling, switching, Wi-Fi and troubleshooting.', level: 'Beginner' },
        { icon: 'fa-video', title: 'CCTV & security installation', text: 'Cameras, recorders, access control and setup.', level: 'Beginner' },
        { icon: 'fa-satellite-dish', title: 'Starlink & satellite installation', text: 'Mounting, alignment, activation and support.', level: 'Beginner' },
        { icon: 'fa-code', title: 'Web development', text: 'HTML, CSS, JavaScript and modern frameworks.', level: 'Beginner – Intermediate' },
        { icon: 'fa-mobile-screen-button', title: 'Mobile app development', text: 'Build and publish cross-platform apps.', level: 'Intermediate' },
        { icon: 'fa-screwdriver-wrench', title: 'Computer maintenance', text: 'Hardware, software installs and repairs.', level: 'Beginner' },
      ],
      perks: ['Certificate of completion', 'Mentorship from industry practitioners', 'Hands-on projects with real equipment', 'Career guidance and placement support'],
    },
    tracker: { icon: 'fa-user-graduate', label: 'Your learning journey' },
    steps: [
      { icon: 'fa-file-pen', when: 'Today', title: 'Apply', text: 'Send the form with your school, interests and preferred start date.', gets: 'Application received' },
      { icon: 'fa-comments', when: 'Within days', title: 'Short interview', text: 'We talk about your goals and match you to a team or course.', gets: 'Your programme & mentor' },
      { icon: 'fa-laptop-code', when: 'Each week', title: 'Learn on real work', text: 'Hands-on projects with a mentor guiding you each week.', gets: 'Real project experience' },
      { icon: 'fa-award', when: 'Completion', title: 'Get certified', text: 'Finish with a certificate, a portfolio and career guidance.', gets: 'Certificate & portfolio' },
    ],
    faqs: [
      { q: 'Who can apply for an internship?', a: 'University and TVET students looking for an academic placement, and graduates or career changers who want practical, job-ready experience.' },
      { q: 'How long are the programmes?', a: 'It depends on the programme and your school’s requirements. We agree the duration and schedule with you before you start.' },
      { q: 'Do I receive a certificate?', a: 'Yes. Every intern and course participant who completes the programme receives a certificate of completion from Fabritech.' },
      { q: 'Do I need my own laptop?', a: 'A laptop helps for software courses. For networking, security and installation courses we provide the equipment you practise on.' },
    ],
    cta: { title: 'Ready to start learning?', text: 'Send your application — we will contact you about the next intake and the right programme for you.' },
  },
]

export const servicePath = (slug) => `/services/${slug}`

/** A service page by slug or by one of its legacy ids, or null. */
export const findServicePage = (id = '') =>
  SERVICE_PAGES.find((page) => page.slug === id || page.legacyIds.includes(id)) || null

/** Page address for a slug or legacy id (falls back to the home page's services section). */
export const servicePathFor = (id) => {
  const page = findServicePage(id)
  return page ? servicePath(page.slug) : '/#services'
}
